import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';

ChartJS.register(...registerables);

const YearWiseDistributionChart = ({ data = [] }) => {
    const barChartRef = useRef(null);
    const barChartInstance = useRef(null);

    // Default data if none provided
    const defaultData = [
        { year: '1st Year', students_involved: 300, projects: 40 },
        { year: '2nd Year', students_involved: 180, projects: 40 },
        { year: '3rd Year', students_involved: 130, projects: 30 },
        { year: '4th Year', students_involved: 180, projects: 50 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.year || item.label);
    const studentsData = chartData.map(item => item.students_involved || item.studentsInvolved || 0);
    const projectsData = chartData.map(item => item.projects || 0);

    const maxValue = Math.max(...studentsData, ...projectsData, 100);
    const stepSize = Math.ceil(maxValue / 4 / 50) * 50 || 50;
    const yMax = Math.ceil(maxValue * 1.2 / stepSize) * stepSize;

    useEffect(() => {
        if (!barChartRef.current) return;

        if (barChartInstance.current) {
            barChartInstance.current.destroy();
        }

        const ctx = barChartRef.current.getContext('2d');
        if (!ctx) return;

        barChartInstance.current = new ChartJS(ctx, {
            type: 'bar',
            data: {
                labels: labels,
                datasets: [
                    {
                        label: 'Students Involved',
                        data: studentsData,
                        backgroundColor: '#E0F5FF',
                        borderColor: '#0C597D',
                        borderWidth: 1,
                        borderRadius: 4,
                        barThickness: 50,
                        maxBarThickness: 50,
                    },
                    {
                        label: 'Projects',
                        data: projectsData,
                        backgroundColor: '#FFF7D9',
                        borderColor: '#A4881A',
                        borderWidth: 1,
                        borderRadius: 4,
                        barThickness: 50,
                        maxBarThickness: 50,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        grid: { display: false },
                        border: { display: true, color: '#e6e6e6' },
                        ticks: {
                            font: { size: 13, family: 'Arial, sans-serif' },
                            color: '#222222',
                        },
                    },
                    y: {
                        beginAtZero: true,
                        max: yMax,
                        ticks: {
                            stepSize: stepSize,
                            font: { size: 13, family: 'Arial, sans-serif' },
                            color: '#444444',
                        },
                        grid: { color: '#f0f0f0', drawBorder: false },
                        border: { display: false },
                    },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        align: 'end',
                        labels: {
                            boxWidth: 26,
                            boxHeight: 13,
                            padding: 12,
                            font: { size: 13, family: 'Arial, sans-serif' },
                            color: '#333333',
                        },
                    },
                    tooltip: { enabled: true },
                    datalabels: { display: false },
                },
            },
        });

        return () => {
            if (barChartInstance.current) {
                barChartInstance.current.destroy();
            }
        };
    }, [data, labels, studentsData, projectsData, yMax, stepSize]);

    const hasData = chartData.some(item =>
        (item.students_involved || item.studentsInvolved || 0) > 0 ||
        (item.projects || 0) > 0
    );

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '8px', flex: 1, minHeight: 0 }}>
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: '"Inter-Medium", "Inter", sans-serif',
                        color: '#333333',
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    No. of Users
                </Typography>

                <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                    {!hasData && data.length === 0 ? (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%'
                        }}>
                            <Typography sx={{ color: '#999', fontSize: '14px' }}>
                                No distribution data available
                            </Typography>
                        </Box>
                    ) : (
                        <canvas ref={barChartRef} style={{ width: '100%', height: '100%', display: 'block' }} />
                    )}
                </Box>
            </Box>

            <Typography
                sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: '"Inter-Medium", "Inter", sans-serif',
                    color: '#333333',
                    textAlign: 'center',
                    flexShrink: 0,
                }}
            >
                Years
            </Typography>
        </Box>
    );
};

export default YearWiseDistributionChart;
